import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { bucketLabel, type InviteForecastMember } from "@/utils/inviteForecast";
import { formatPhoneNumber } from "@/utils/phoneFormatter";

interface MembersProps {
  members: InviteForecastMember[];
  /** Everyone in the cohort; larger than members.length only past the cap. */
  total: number;
  timezone: string;
}

/** "Mon 11:55 AM" in the store's timezone. */
function formatOrderedAt(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
  }).format(new Date(iso));
}

/**
 * "Who's in this batch": every customer in today's cohort with their name,
 * full phone number, when they last ordered and whether they get the invite
 * (or which rule leaves them out). Collapsed by default so the hero stays
 * glanceable; read-only.
 */
export function InviteForecastMembers({ members, total, timezone }: MembersProps) {
  const [open, setOpen] = useState(false);
  if (total === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex min-h-11 w-full items-center justify-between gap-2 rounded-md text-left text-sm font-medium text-foreground hover:text-action-primary">
        <span>
          Who&rsquo;s in this batch ({total})
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="max-h-80 divide-y divide-border overflow-y-auto">
          {members.map((m) => (
            <li key={m.customer_phone} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{m.display_name || "No name on file"}</p>
                <p className="text-xs text-muted-foreground">
                  <span className="tabular-nums">{formatPhoneNumber(m.customer_phone)}</span> · ordered{" "}
                  {formatOrderedAt(m.last_order_at, timezone)}
                </p>
              </div>
              <span
                className={`text-sm ${m.bucket === "will_invite" ? "text-status-success" : "text-muted-foreground"}`}
              >
                {bucketLabel(m.bucket)}
              </span>
            </li>
          ))}
        </ul>
        {total > members.length && (
          <p className="pt-2 text-xs text-muted-foreground">
            Showing the {members.length} most recent of {total}.
          </p>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
